import { Document } from './document';
import { DocumentType } from './document-type.enum';
import { OrderStatus } from './order-status.enum';

/**
 * Represents all of the data associated with a single order.
 */
export class Order {
    /** unsaved: there are unsaved changes to this order */
    unsaved = false;
    /** index: the index of this order in the User's 'Orders' array */
    index: number;
    /** newOrder: this is an order that is only in local storage, not yet in Dynamo */
    newOrder = false;

    // Order types
    justDocuments: boolean;
    requestForQuotation: boolean;
    resubmission: boolean;

    SwordsID: string;
    FlandersID: string;
    LlanberisID: string;
    LasAngelesID: string;
    name: string;
    status: OrderStatus;
    startedDate: Date;
    submissionDate: Date;
    documents: Map<string, Document[]>;

    // inputs
    SMN: string;
    revisionNumber: string;
    quantity: number;
    shipDate: Date;
    FlandersPO: string;
    SwordsPO: string;
    selectFlanders: boolean;
    selectSwords: boolean;
    LlanberisPO: string;
    LasAngelesPO: string;
    selectLlanberis: boolean;
    selectLasAngeles: boolean;
    comments: string;
    contactName: string;
    contactEmail: string;
    question1Response: boolean;
    question2Response: boolean;

    /**
     * Constructs a new Order object with the given fields.
     */
    constructor(name: string, status: OrderStatus, startedDate: Date, justDocuments: boolean, resubmission: boolean) {
        this.name = name;
        this.status = status;
        this.startedDate = startedDate;
        this.justDocuments = justDocuments;
        this.resubmission = resubmission;
        this.documents = new Map<string, Document[]>();
    }

    /**
     * Converts an array of (orders in JSON form) to an Order[]
     *
     * @param ordersJson the array of (orders in JSON form)
     *
     * @return the new array of Orders
     */
    static jsonToOrders(ordersJson: any[]): Order[] {
        const arr: Order[] = [];
        if (ordersJson) {
            ordersJson.forEach((value, index: number) => {
                const newOrder = new Order(
                    value.name,
                    value.status,
                    new Date(value.started_date),
                    value.just_documents,
                    value.resubmission
                );
                newOrder.FlandersID = value.FlandersID;
                newOrder.SwordsID = value.SwordsID;
                newOrder.LlanberisID = value.LlanberisID;
                newOrder.LasAngelesID = value.LasAngelesID;
                newOrder.index = index;
                newOrder.submissionDate = value.submission_date ? new Date(value.submission_date) : null;
                newOrder.documents = Document.jsonToDocuments(value.documents);
                newOrder.SMN = value.SMN;
                newOrder.revisionNumber = value.SMN_revision;
                newOrder.quantity = value.quantity;
                newOrder.shipDate = value.ship_date;
                newOrder.FlandersPO = value.PO_Flanders;
                newOrder.SwordsPO = value.PO_Swords;
                newOrder.LasAngelesPO = value.PO_LasAngeles;
                newOrder.LlanberisPO = value.PO_Llanberis;
                newOrder.selectFlanders = value.select_Flanders;
                newOrder.selectSwords = value.select_Swords;
                newOrder.selectLlanberis = value.select_Llanberis;
                newOrder.selectLasAngeles = value.select_LasAngeles;
                newOrder.comments = value.comments;
                newOrder.contactName = value.contact_name;
                newOrder.contactEmail = value.contact_email;
                newOrder.requestForQuotation = value.request_for_quotation;
                newOrder.question1Response = value.question_1_response;
                newOrder.question2Response = value.question_2_response;
                arr.push(newOrder);
            });
        }
        return arr;
    }

    /**
     * Converts an Order to an order object in JSON form as a string
     *
     * @param order the order to be converted
     *
     * @return the object JSON string
     */
    static orderToJson(order: Order): string {
        const json = {
            FlandersID: order.FlandersID,
            SwordsID: order.SwordsID,
            LlanberisID: order.LlanberisID,
            LasAngelesID: order.LasAngelesID,
            documents: Document.documentsToObject(order.documents),
            name: order.name,
            comments: order.comments,
            contact_name: order.contactName,
            contact_email: order.contactEmail,
            PO_Flanders: order.FlandersPO,
            PO_Swords: order.SwordsPO,
            PO_LasAngeles: order.LasAngelesPO,
            PO_Llanberis: order.LlanberisPO,
            quantity: order.quantity,
            select_Flanders: order.selectFlanders,
            select_Swords: order.selectSwords,
            select_LasAngeles: order.selectLasAngeles,
            select_Llanberis: order.selectLlanberis,
            ship_date: order.shipDate,
            SMN: order.SMN,
            SMN_revision: order.revisionNumber,
            started_date: order.startedDate,
            status: order.status,
            submission_date: order.submissionDate,
            just_documents: order.justDocuments,
            request_for_quotation: order.requestForQuotation,
            resubmission: order.resubmission,
            question_1_response: order.question1Response,
            question_2_response: order.question2Response
        };
        for (const property in json) {
            if (json[property] === '') {
                delete json[property];
            }
        }
        return JSON.stringify(json);
    }

    /**
     * Makes a deep copy of an order's documents, cloning each document.
     *
     * @return the new copy of the document map
     */
    cloneDocumentMap(): Map<string, Document[]> {
        const map = new Map<string, Document[]>();
        this.documents.forEach((value: Document[], key: string) => {
            const newDocs: Document[] = [];
            value.forEach((doc: Document) => {
                newDocs.push(doc.clone());
            });
            map.set(key, newDocs);
        });
        return map;
    }

    /**
     * Makes a deep copy of an order.
     *
     * @return the new copy of the order
     */
    clone(): Order {
        const newOrder = new Order(this.name, this.status, this.startedDate, this.justDocuments, this.resubmission);
        newOrder.FlandersID = null;
        newOrder.SwordsID = null;
        newOrder.LlanberisID = null;
        newOrder.LasAngelesID = null;
        newOrder.index = this.index;
        newOrder.submissionDate = this.submissionDate;
        newOrder.documents = this.cloneDocumentMap();
        newOrder.SMN = this.SMN;
        newOrder.revisionNumber = this.revisionNumber;
        newOrder.quantity = this.quantity;
        newOrder.shipDate = this.shipDate;
        newOrder.FlandersPO = this.FlandersPO;
        newOrder.SwordsPO = this.SwordsPO;
        newOrder.LasAngelesPO = this.LasAngelesPO;
        newOrder.LlanberisPO = this.LlanberisPO;
        newOrder.selectFlanders = this.selectFlanders;
        newOrder.selectSwords = this.selectSwords;
        newOrder.selectLlanberis = this.selectLlanberis;
        newOrder.selectLasAngeles = this.selectLasAngeles;
        newOrder.comments = this.comments;
        newOrder.contactName = this.contactName;
        newOrder.contactEmail = this.contactEmail;
        newOrder.requestForQuotation = this.requestForQuotation;
        newOrder.question1Response = this.question1Response;
        newOrder.question2Response = this.question2Response;
        newOrder.unsaved = this.unsaved;
        newOrder.newOrder = this.newOrder;
        return newOrder;
    }

    /**
     * Checks if an order is ready to be submitted. If there are any problems, an error
     * is thrown detailing each error.
     *
     * @throws Error if any validation condition is not met.
     */
    validate(): void {
        let errorMessage = '';
        if (!this.name) {
            errorMessage += 'Order title is required.\n';
        }
        if (!this.contactName) {
            errorMessage += 'Contact name is required.\n';
        }
        if (!this.contactEmail) {
            errorMessage += 'Contact email is required.\n';
        }
        if (this.contactEmail === undefined || !this.contactEmail.match(/.@.+\./)) {
            errorMessage += 'Contact email must be an email.\n';
        }
        if (this.resubmission && !this.comments) {
            errorMessage += 'Comments are required on resubmissions.\n';
        }
        if (!this.SMN || isNaN(+this.SMN) || this.SMN.length !== 8) {
            errorMessage += 'SMN must be a 8 digit number.\n';
        }
        if (!this.revisionNumber) {
            errorMessage += 'SMN Revision is required.\n';
        }
        if (this.revisionNumber && this.revisionNumber.length > 2) {
            errorMessage += 'SMN Revision must be 2 characters or less.\n';
        }
        if (!this.selectSwords && !this.selectFlanders && !this.selectLasAngeles && !this.selectLlanberis) {
            errorMessage += 'At least one site must be selected.\n';
        }
        if (this.justDocuments) {
            if (this.requestForQuotation === undefined) {
                errorMessage += 'All questions must be answered.\n';
            }
            if (
                this.requestForQuotation &&
                (!this.documents.get(DocumentType.RFQ) || this.documents.get(DocumentType.RFQ).length === 0)
            ) {
                errorMessage += 'If this is a Request For Quotation, then a document is required.\n';
            }
        } else {
            if (!this.quantity) {
                errorMessage += 'Quantity is required.\n';
            }
            if (this.quantity && (this.quantity <= 0 || isNaN(this.quantity))) {
                errorMessage += 'Quantity must be a positive number.\n';
            }
            if (!this.shipDate) {
                errorMessage += 'Ship Date is required.\n';
            }
            if (this.selectFlanders && !this.FlandersPO) {
                errorMessage += 'Flanders PO is required if Flanders is selected.\n';
            }
            if (this.selectFlanders && this.FlandersPO && (isNaN(+this.FlandersPO) || this.FlandersPO.length !== 10)) {
                errorMessage += 'Flanders PO must be a 10 digit number.\n';
            }
            if (this.selectSwords && !this.SwordsPO) {
                errorMessage += 'Swords PO is required if Swords is selected.\n';
            }
            if (this.selectSwords && this.SwordsPO && (isNaN(+this.SwordsPO) || this.SwordsPO.length !== 10)) {
                errorMessage += 'Swords PO must be a 10 digit number.\n';
            }
            if (this.selectLlanberis && !this.LlanberisPO) {
                errorMessage += 'Llanberis PO is required if Llanberis is selected.\n';
            }
            if (
                this.selectLlanberis &&
                this.LlanberisPO &&
                (isNaN(+this.LlanberisPO) || this.LlanberisPO.length !== 10)
            ) {
                errorMessage += 'Llanberis PO must be a 10 digit number.\n';
            }
            if (this.selectLasAngeles && !this.selectLasAngeles) {
                errorMessage += 'Las Angeles PO is required if Las Angeles is selected.\n';
            }
            if (
                this.selectLasAngeles &&
                this.LasAngelesPO &&
                (isNaN(+this.LasAngelesPO) || this.LasAngelesPO.length !== 10)
            ) {
                errorMessage += 'Las Angeles PO must be a 10 digit number.\n';
            }
            if (this.question1Response === undefined || this.question2Response === undefined) {
                errorMessage += 'All questions must be answered.\n';
            }
            if (
                (this.question1Response === true || this.question2Response === true) &&
                (!this.documents.get(DocumentType.FAIR) || this.documents.get(DocumentType.FAIR).length === 0)
            ) {
                errorMessage += 'A FAIR document is required.\n';
            }
            if (!this.documents.get(DocumentType.COC) || this.documents.get(DocumentType.COC).length === 0) {
                errorMessage += 'A Certificate of Conformance document is required.\n';
            }
        }
        if (errorMessage) {
            throw new Error('\n\n' + errorMessage);
        }
    }

    /**
     * Decides whether an order's COC needs to contain a rework statement.
     *
     * @return whether or not the order requires a rework statement in its COC
     */
    isReworkRequired(): boolean {
        return (
            (this.selectSwords &&
                this.SwordsPO &&
                (this.SwordsPO.toString().substring(0, 2) === '32' ||
                    this.SwordsPO.toString().substring(0, 2) === '38')) ||
            (this.selectFlanders &&
                this.FlandersPO &&
                (this.FlandersPO.toString().substring(0, 3) === '229' ||
                    this.FlandersPO.toString().substring(0, 3) === '281'))
        );
    }

    /**
     * Decides whether an order requires a FAIR based on their current selections.
     *
     * @return whether or not the order requires a FAIR
     */
    needsFirstArticle(): boolean {
        return this.question1Response || this.question2Response;
    }
}
